import React from "react";
import { Phone, Mail, Users, Sparkles, FileText } from "lucide-react";
import { LogCallButton, OutcomeBadge, CallHistory } from "@/components/admin/LogCall";

export const STATUSES = [
  { key: "new", label: "New", cls: "bg-royal/10 text-royal border-royal/20" },
  { key: "contacted", label: "Contacted", cls: "bg-gold/15 text-[#8a6500] border-gold/30" },
  { key: "converted", label: "Converted", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];
const statusCls = (s) => (STATUSES.find((x) => x.key === s) || STATUSES[0]).cls;

export const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export const sourceLabel = (src) => {
  if (!src) return "-";
  if (src.startsWith("guide:")) return `Guide · ${src.slice(6).replace(/-/g, " ")}`;
  return src.replace(/-/g, " ");
};

export const isGuideLead = (l) => (l.source || "").startsWith("guide:");

export const StatusSelect = ({ lead, onChange }) => (
  <select
    data-testid={`lead-status-${lead.id}`}
    value={lead.status || "new"}
    onChange={(e) => onChange(lead.id, e.target.value)}
    className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold outline-none transition-colors focus:ring-2 focus:ring-royal/20 ${statusCls(lead.status)}`}
  >
    {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
  </select>
);

const STAT_CARDS = [
  { key: "all", label: "Total enquiries", icon: Users, cls: "bg-navy text-gold" },
  { key: "new", label: "New", icon: Sparkles, cls: "bg-royal/10 text-royal" },
  { key: "contacted", label: "Contacted", icon: Phone, cls: "bg-gold/15 text-[#8a6500]" },
  { key: "converted", label: "Converted", icon: Mail, cls: "bg-emerald-50 text-emerald-700" },
  { key: "guides", label: "Guide downloads", icon: FileText, cls: "bg-lightbg text-navy" },
];

export const countLeads = (leads) => ({
  all: leads.length,
  new: leads.filter((l) => (l.status || "new") === "new").length,
  contacted: leads.filter((l) => l.status === "contacted").length,
  converted: leads.filter((l) => l.status === "converted").length,
  guides: leads.filter(isGuideLead).length,
});

export const StatCards = ({ counts, filter, onFilter }) => (
  <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
    {STAT_CARDS.map(({ key, label, icon: IconC, cls }) => (
      <button
        key={key}
        data-testid={`lead-filter-${key}`}
        onClick={() => onFilter(key)}
        className={`flex items-center gap-3 rounded-2xl border bg-white p-4 text-left transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-hover ${filter === key ? "border-royal ring-2 ring-royal/15" : "border-navy/[0.08]"}`}
      >
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${cls}`}><IconC className="h-5 w-5" /></span>
        <div>
          <p className="font-display text-xl font-extrabold text-navy" data-testid={`lead-count-${key}`}>{counts[key]}</p>
          <p className="text-xs text-ink/55">{label}</p>
        </div>
      </button>
    ))}
  </div>
);

const COLUMNS = ["Status", "Name", "Contact", "Age", "Income", "Investment", "Goal", "Source", "Received", "Call"];

export const LeadsTable = ({ leads, onStatus, onLogged }) => (
  <div className="hidden overflow-hidden rounded-2xl border border-navy/[0.08] bg-white lg:block">
    <table className="w-full text-left text-sm" data-testid="leads-table">
      <thead className="bg-lightbg text-xs font-semibold uppercase tracking-wide text-ink/50">
        <tr>{COLUMNS.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-navy/[0.06]">
        {leads.map((l) => (
          <tr key={l.id} data-testid={`lead-row-${l.id}`} className="align-top transition-colors hover:bg-lightbg/60">
            <td className="px-4 py-3"><StatusSelect lead={l} onChange={onStatus} /></td>
            <td className="px-4 py-3 font-semibold text-navy">{l.name}<div className="mt-1"><OutcomeBadge lead={l} /></div><CallHistory lead={l} /></td>
            <td className="px-4 py-3 text-ink/70">
              <div className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-royal" /> {l.mobile}</div>
              <div className="mt-1 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-royal" /> {l.email}</div>
            </td>
            <td className="px-4 py-3 text-ink/70">{l.age || "-"}</td>
            <td className="px-4 py-3 text-ink/70">{l.monthly_income || "-"}</td>
            <td className="px-4 py-3 text-ink/70">{l.monthly_investment || "-"}</td>
            <td className="px-4 py-3 text-ink/70">{l.primary_goal || "-"}{l.existing_investments ? <span className="block text-xs text-ink/45">Existing: {l.existing_investments}</span> : null}</td>
            <td className="px-4 py-3 text-xs capitalize text-ink/60">{sourceLabel(l.source)}</td>
            <td className="px-4 py-3 text-ink/50">{fmtDate(l.created_at)}</td>
            <td className="px-4 py-3"><LogCallButton lead={l} onLogged={onLogged} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DETAILS = [["Age", "age"], ["Income", "monthly_income"], ["Investment", "monthly_investment"], ["Goal", "primary_goal"], ["Existing", "existing_investments"]];

export const LeadCards = ({ leads, onStatus, onLogged }) => (
  <div className="space-y-4 lg:hidden">
    {leads.map((l) => (
      <div key={l.id} data-testid={`lead-card-${l.id}`} className="rounded-2xl border border-navy/[0.08] bg-white p-5">
        <div className="flex items-center justify-between gap-3">
          <p className="font-display text-lg font-bold text-navy">{l.name}</p>
          <StatusSelect lead={l} onChange={onStatus} />
        </div>
        <p className="mt-1 text-xs text-ink/45">{fmtDate(l.created_at)} · <span className="capitalize">{sourceLabel(l.source)}</span></p>
        <div className="mt-2 flex flex-wrap items-center gap-2"><LogCallButton lead={l} onLogged={onLogged} /><OutcomeBadge lead={l} /></div>
        <CallHistory lead={l} />
        <div className="mt-2 space-y-1 text-sm text-ink/70">
          <a href={`tel:${l.mobile}`} className="flex items-center gap-2"><Phone className="h-4 w-4 text-royal" /> {l.mobile}</a>
          <a href={`mailto:${l.email}`} className="flex items-center gap-2"><Mail className="h-4 w-4 text-royal" /> {l.email}</a>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          {DETAILS.map(([label, field]) => (
            <div key={field} className="rounded-lg bg-lightbg px-3 py-2">
              <p className="font-semibold uppercase tracking-wide text-ink/40">{label}</p>
              <p className="mt-0.5 text-ink/75">{l[field] || "-"}</p>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);
